// @givmo/board — a director's act through the shared "Confirm it's you" sheet (CBG-SEC2 SEC2-WEB).
//
// THE LAPSE (derivation §8.3, contract §6). Each act is a THUNK over the payload already captured —
// the content-hash echo, the intent statement's id, a disclosure's typed fields — and runs through
// `@givmo/step-up`'s gate. The backend decides whether the window is open, afresh for every act; the
// page never predicts it. When it answers `board_director_signing_required`:
//   1. the sheet opens by itself (Jonathan, D-CBG-18 answer 2: no "Confirm to sign" anywhere);
//   2. after the confirmation the gate force-refreshes the ID token (the sheet's passkey arm also
//      re-issues the sign-in, Account Security's slot), so the retry can't carry the refused token;
//   3. the SAME thunk is sent once more: the same hash, the same intent id, nothing typed lost;
//   4. a second refusal comes back as the operation's own error, and Board shows its inline line —
//      never a loop.
//
// WHICH WAY IN (Account Security, 2026-10-02 22:43 PDT, answer 2): a passkey account confirms only
// with its passkey (rule 3: an authenticator beside a passkey opens nothing); an account without one
// confirms with a re-sign-in that Identity Platform answers with its authenticator challenge. Board
// never names the password: the host's ways in drop it at AS-PWL-10, in one place.
//
// WHICH WORDS. Board's refusal carries no `context.action`, so each gate names its own row through
// the code-to-row map (Account Security's AS-WEB10): `{ board_director_signing_required: <row> }`.
// Every Board surface keeps its own words, and none falls through to the package's payout fallback
// (contract §14; the orchestrator, 2026-10-03 00:31 PDT).

import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  STEP_UP_PREPARE_WAIT_MS,
  STEP_UP_WAYS_IN,
  StepUpPasskeyFailure,
  isStepUpCancelled,
  useOptionalStepUpHost,
  useStepUpGate,
  type StepUpClientRefusal,
  type StepUpHostAdapter,
  type StepUpRefusalCodes,
  type StepUpWayIn,
} from '@givmo/step-up';
import { useRef, type ReactNode } from 'react';
import { DIRECTOR_REFUSALS } from './director-wire';
import { BOARD_SIGNING_COPY, type BoardSigningRow } from './signing-copy';

const WITHOUT_PASSKEY: readonly StepUpWayIn[] = STEP_UP_WAYS_IN.filter((way) => way !== 'passkey');

/**
 * The ways in a prompt for Board's window accepts. Unknown (`/me` unread, or the account's factors
 * unreadable) narrows nothing: the person meets the prompt with what they hold, and the retry decides.
 */
export function signingAccepts(
  accountHasPasskey: boolean | null | undefined,
): readonly StepUpWayIn[] | undefined {
  if (accountHasPasskey === true) return ['passkey'];
  if (accountHasPasskey === false) return WITHOUT_PASSKEY;
  return undefined;
}

/**
 * Whether the account holds an authenticator app, from the sheet's own host adapter — the source
 * the sheet chooses its arms from (the orchestrator's decision (b), 2026-10-02 22:44 PDT). Unknown
 * while the host can't say: the server's read not served, not made yet (a host that reads on demand,
 * AS-WEB15), or still on the wire (`preparing`).
 */
export function holdsAuthenticatorOf(host: StepUpHostAdapter | null): boolean | null {
  if (host?.preparing === true) return null;
  return host?.holdsAuthenticator ?? null;
}

/**
 * The nearest step-up host as of the LATEST render. A host that reads the account on demand
 * (AS-WEB15) re-renders with the account's answer once its `prepare` settles, so a decision taken
 * after awaiting it must read the adapter afresh, never the one its handler closed over.
 */
export function useLatestStepUpHost(): { readonly current: StepUpHostAdapter | null } {
  const host = useOptionalStepUpHost();
  const ref = useRef(host);
  ref.current = host;
  return ref;
}

/** Wait for `work` to settle, never longer than `ms`. Never rejects. */
function settledWithin(work: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    work.then(done, done);
  });
}

/**
 * Whether the account holds an authenticator app, read the way the sheet will read it: a host that
 * reads the account on demand is asked first (`prepare`), so Sign's choice between Account
 * Security's setup offer and a prompt rests on the account's answer, never on "not read yet". Unknown
 * when the host can't say. Never rejects.
 *
 * ⚠ BOUNDED, as the gate's own wait is (`STEP_UP_PREPARE_WAIT_MS`): a read that never settles must
 * not hold Sign, the invitation's join, or a disclosure dialog that can't close while an act is
 * pending. Past the bound the answer is still being read, so it is unknown, and unknown takes the
 * sheet path (review-43 H1).
 */
export async function readHoldsAuthenticator(hostRef: {
  readonly current: StepUpHostAdapter | null;
}): Promise<boolean | null> {
  const prepare = hostRef.current?.prepare;
  if (prepare !== undefined) {
    // A failed read leaves the answer unknown; a slow one is waited for only so long.
    await settledWithin(
      Promise.resolve().then(() => prepare()),
      STEP_UP_PREPARE_WAIT_MS,
    );
  }
  return holdsAuthenticatorOf(hostRef.current);
}

/**
 * Sign opens Account Security's setup offer INSTEAD of the sheet only when the account has neither
 * factor, both facts read as strictly false. Unknown takes the sheet path (contract §5.4).
 */
export function needsFactorSetup(
  accountHasPasskey: boolean | null | undefined,
  holdsAuthenticator: boolean | null,
): boolean {
  return accountHasPasskey === false && holdsAuthenticator === false;
}

/** How one act through the gate ended. */
export type SigningOutcome<T> =
  | { kind: 'done'; value: T }
  /** The director dismissed the sheet: nothing was signed, and the sheet already said so. */
  | { kind: 'cancelled' }
  /** Refused again after a confirmation: Board's inline line, never a loop. */
  | { kind: 'refused-twice' }
  /** A passkey-only prompt found no passkey after all: the setup offer, not the sheet. */
  | { kind: 'needs-setup' }
  /** Anything else — a changed text (409), a changed sentence (409), a transport failure. */
  | { kind: 'failed'; error: unknown };

/** The refusal's code answered with a prompt, worded by this gate's row (see the header). */
function refusalCodesFor(row: BoardSigningRow): StepUpRefusalCodes {
  return { [DIRECTOR_REFUSALS.signingRequired]: row };
}

/**
 * CBG-RC12-READ — the window asked for before a read the backend answers WITHOUT refusing. The
 * director's meeting record withholds a closed session's own facts while the window is shut (backend
 * `board_minutes.md` §O12 C5) rather than refusing the read, so no server answer would raise the
 * sheet. An opening's thunk throws this while `/me` still says the window is shut; the gate answers
 * it as the server's signing refusal (this gate's row), then runs the thunk once more, after the
 * confirmation and the token refresh. Any other gate's thunk never throws it.
 */
export class BoardSigningWindowShut extends Error {
  constructor() {
    super('The signing window is shut.');
    this.name = 'BoardSigningWindowShut';
  }
}

/** {@link BoardSigningWindowShut}, answered like the server's refusal, worded by `row`. */
function clientRefusalFor(row: BoardSigningRow): StepUpClientRefusal {
  return (error) => (error instanceof BoardSigningWindowShut ? { row } : null);
}

export interface BoardSigningGate {
  /**
   * Render inside the page: the sheet, while a confirmation is outstanding. Always `null` under a
   * host's `StepUpHostProvider`, which draws the ONE sheet for every gate beneath it
   * (`@givmo/step-up`'s one prompt per host), so it says nothing about whether a sheet is up.
   */
  dialog: ReactNode;
  /**
   * Whether the act waits on the director at the sheet now (`@givmo/step-up`'s `prompting`,
   * CCM-R1-STEP-UP-C): from asking for the prompt until that wait ends. False during the act, the
   * host's account read before the sheet, the token refresh and the retry. An act is busy while it
   * is pending and not this, so Sign rests behind the sheet (frames `w-lapse-*`) and spins through
   * the two windows a refusal leaves without one.
   */
  prompting: boolean;
  /** Run one act through the gate. Never rejects: every ending is an outcome. */
  run: <T>(
    operation: () => Promise<T>,
    accountHasPasskey: boolean | null | undefined,
  ) => Promise<SigningOutcome<T>>;
}

/** One surface's gate, its sheet worded by `row`. */
export function useBoardSigningGate(row: BoardSigningRow): BoardSigningGate {
  const gate = useStepUpGate({
    copy: BOARD_SIGNING_COPY,
    testIdPrefix: 'board-signing',
    refusalCodes: refusalCodesFor(row),
    clientRefusal: clientRefusalFor(row),
  });

  const run = async <T,>(
    operation: () => Promise<T>,
    accountHasPasskey: boolean | null | undefined,
  ): Promise<SigningOutcome<T>> => {
    const accepts = signingAccepts(accountHasPasskey);
    try {
      const value = await gate.run(operation, accepts === undefined ? {} : { accepts });
      return { kind: 'done', value };
    } catch (error) {
      if (isStepUpCancelled(error)) return { kind: 'cancelled' };
      if (error instanceof StepUpPasskeyFailure && error.reason === 'none_registered') {
        return { kind: 'needs-setup' };
      }
      if (isGivmoError(error) && firstErrorCode(error) === DIRECTOR_REFUSALS.signingRequired) {
        return { kind: 'refused-twice' };
      }
      // Still shut after a confirmation: the same dead end as the server's second refusal.
      if (error instanceof BoardSigningWindowShut) return { kind: 'refused-twice' };
      return { kind: 'failed', error };
    }
  };

  return { dialog: gate.dialog, prompting: gate.prompting, run };
}
